import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BoltIcon } from "../components/Bolts";
import {
    getManageCategories,
    getManageThemes,
    createCategory,
    createSubcategory,
    createTheme,
    uploadSubcategoryImage,
    uploadThemeImage,
    hideItem,
    unhideItem,
    refreshDynamicCategories,
} from "../lib/dynamicCatalog";
import { ArrowLeft, ChevronRight, Eye, EyeOff, ImagePlus, FolderTree, Sparkles, Plus, Loader2 } from "lucide-react";

// view = "root" | "categories" | "category" | "themes"
const BoneyardPage = () => {
    const [ready, setReady] = useState(false);
    const [cats, setCats] = useState([]);
    const [themes, setThemes] = useState([]);
    const [view, setView] = useState("root");
    const [activeCat, setActiveCat] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = () => {
        setCats(getManageCategories());
        setThemes(getManageThemes());
    };

    useEffect(() => {
        refreshDynamicCategories().then(() => {
            reload();
            setReady(true);
        });
    }, []);

    const run = async (fn) => {
        setBusy(true);
        try {
            await fn();
            reload();
        } catch (e) {
            alert("Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    if (!ready) {
        return <div className="min-h-[60vh] flex items-center justify-center text-storm-mist">Loading the Boneyard…</div>;
    }

    const currentCat = activeCat ? cats.find((c) => c.slug === activeCat) : null;

    return (
        <div data-testid="boneyard-page" className="min-h-[80vh]">
            <section className="border-b border-white/10 bg-storm-navy">
                <div className="container-x py-14 md:py-16">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-5 hover:gap-3 transition-all">
                        <ArrowLeft className="w-4 h-4" /> Admin
                    </Link>
                    <p className="eyebrow flex items-center gap-2 mb-4">
                        <BoltIcon className="w-3.5 h-3.5" /> Site Structure
                    </p>
                    <h1 className="text-4xl md:text-6xl text-white leading-[0.9]">
                        The <span className="text-storm-bolt">Boneyard</span>
                    </h1>
                    <p className="text-storm-mist mt-3 max-w-xl">
                        Categories, subcategories, and themes — add, hide, unhide, and set tile photos. Nothing here is ever fully deleted, only hidden and reversible.
                    </p>

                    {/* Breadcrumb */}
                    <div className="flex items-center gap-2 mt-8 text-sm">
                        <Crumb label="Boneyard" active={view === "root"} onClick={() => { setView("root"); setActiveCat(null); }} />
                        {view !== "root" && (
                            <>
                                <ChevronRight className="w-3.5 h-3.5 text-storm-mist" />
                                {view === "themes" ? (
                                    <Crumb label="Themes" active testid="crumb-themes" />
                                ) : (
                                    <Crumb label="Categories" active={view === "categories"} onClick={() => { setView("categories"); setActiveCat(null); }} />
                                )}
                            </>
                        )}
                        {view === "category" && currentCat && (
                            <>
                                <ChevronRight className="w-3.5 h-3.5 text-storm-mist" />
                                <Crumb label={currentCat.name} active testid="crumb-category" />
                            </>
                        )}
                    </div>
                </div>
            </section>

            <section className="container-x py-10 md:py-14">
                {view === "root" && (
                    <div className="grid sm:grid-cols-2 gap-5 max-w-2xl">
                        <RootCard
                            icon={<FolderTree className="w-5 h-5" />}
                            title="Categories"
                            desc="Apparel, Stickers, Woodworking & more — with their subcategories."
                            onClick={() => setView("categories")}
                            testid="boneyard-open-categories"
                        />
                        <RootCard
                            icon={<Sparkles className="w-5 h-5" />}
                            title="Themes"
                            desc="Pokémon, Video Games & any theme you've created — used across every category."
                            onClick={() => setView("themes")}
                            testid="boneyard-open-themes"
                        />
                    </div>
                )}

                {view === "categories" && (
                    <CategoryList
                        cats={cats}
                        busy={busy}
                        onOpen={(slug) => { setActiveCat(slug); setView("category"); }}
                        onHide={(c) => run(() => (c.hidden ? unhideItem("category", c.slug) : hideItem("category", c.slug)))}
                        onAdd={(name) => run(() => createCategory(name))}
                    />
                )}

                {view === "category" && currentCat && (
                    <CategoryDetail
                        cat={currentCat}
                        busy={busy}
                        onBack={() => { setView("categories"); setActiveCat(null); }}
                        onHideCategory={() => run(() => (currentCat.hidden ? unhideItem("category", currentCat.slug) : hideItem("category", currentCat.slug)))}
                        onHideSub={(s) => run(() => (s.hidden ? unhideItem("subcategory", s.slug, currentCat.slug) : hideItem("subcategory", s.slug, currentCat.slug)))}
                        onAddSub={(name) => run(() => createSubcategory(currentCat.slug, name))}
                        onUploadImage={(subSlug, file) => run(() => uploadSubcategoryImage(currentCat.slug, subSlug, file))}
                    />
                )}

                {view === "themes" && (
                    <ThemeList
                        themes={themes}
                        busy={busy}
                        onHide={(t) => run(() => (t.hidden ? unhideItem("collection", t.slug) : hideItem("collection", t.slug)))}
                        onAdd={(name) => run(() => createTheme(name))}
                        onUploadImage={(slug, file) => run(() => uploadThemeImage(slug, file))}
                    />
                )}
            </section>
        </div>
    );
};

const Crumb = ({ label, active, onClick, testid }) => (
    <button
        onClick={onClick}
        disabled={!onClick}
        data-testid={testid}
        className={`uppercase tracking-wider text-xs ${active ? "text-storm-bolt" : "text-storm-mist hover:text-white"} ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
        {label}
    </button>
);

const RootCard = ({ icon, title, desc, onClick, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className="surface-solid p-6 md:p-7 text-left flex items-start justify-between gap-4 group hover:border-storm-bolt/40 transition-colors"
    >
        <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-11 h-11 bg-storm-bolt text-black shrink-0">
                {icon}
            </span>
            <div>
                <div className="font-heading text-xl text-white">{title}</div>
                <div className="text-sm text-storm-mist mt-1">{desc}</div>
            </div>
        </div>
        <ChevronRight className="w-5 h-5 text-storm-bolt shrink-0 group-hover:translate-x-1 transition-transform mt-2" />
    </button>
);

const HiddenTag = () => (
    <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-sm tracking-wider uppercase text-storm-mist">Hidden</span>
);

const HideToggle = ({ hidden, onClick, busy, testid, small }) => (
    <button
        onClick={onClick}
        disabled={busy}
        data-testid={testid}
        title={hidden ? "Unhide — show it on the site again" : "Hide — pull it off the site (reversible)"}
        className={`inline-flex items-center gap-1 uppercase tracking-wider text-storm-mist hover:text-storm-bolt disabled:opacity-50 ${small ? "text-xs" : "text-sm"}`}
    >
        {hidden ? <><Eye className="w-4 h-4" /> Unhide</> : <><EyeOff className="w-4 h-4" /> Hide</>}
    </button>
);

const AddInline = ({ placeholder, onAdd, busy, testid }) => {
    const [name, setName] = useState("");
    const [open, setOpen] = useState(false);

    if (!open) {
        return (
            <button
                onClick={() => setOpen(true)}
                data-testid={testid}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-storm-bolt hover:gap-3 transition-all"
            >
                <Plus className="w-4 h-4" /> Add new
            </button>
        );
    }

    const submit = async () => {
        if (!name.trim()) return;
        await onAdd(name.trim());
        setName("");
        setOpen(false);
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={placeholder}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                className="bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt min-w-[200px]"
            />
            <button onClick={submit} disabled={busy || !name.trim()} className="btn-primary !text-xs !px-3 !py-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </button>
            <button onClick={() => { setOpen(false); setName(""); }} className="text-storm-mist text-xs hover:text-white">Cancel</button>
        </div>
    );
};

const CategoryList = ({ cats, busy, onOpen, onHide, onAdd }) => (
    <div className="space-y-3 max-w-2xl">
        {cats.map((c) => (
            <div key={c.slug} className={`surface-solid p-5 flex items-center justify-between gap-4 ${c.hidden ? "opacity-50" : ""}`} data-testid={`boneyard-cat-${c.slug}`}>
                <button onClick={() => onOpen(c.slug)} className="flex items-center gap-3 text-left group flex-1 min-w-0">
                    <span className="font-heading text-lg text-white truncate">{c.name}</span>
                    {c.hidden && <HiddenTag />}
                    <span className="text-xs text-storm-mist">({c.sub.length} sub)</span>
                    <ChevronRight className="w-4 h-4 text-storm-bolt group-hover:translate-x-1 transition-transform shrink-0 ml-auto" />
                </button>
                <div className="shrink-0 ml-3">
                    <HideToggle hidden={c.hidden} busy={busy} testid={`boneyard-hide-cat-${c.slug}`} onClick={() => onHide(c)} />
                </div>
            </div>
        ))}
        <div className="pt-2">
            <AddInline placeholder="New category name" busy={busy} onAdd={onAdd} testid="boneyard-add-category" />
        </div>
    </div>
);

const CategoryDetail = ({ cat, busy, onBack, onHideCategory, onHideSub, onAddSub, onUploadImage }) => (
    <div className="max-w-3xl">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt mb-6 hover:gap-3 transition-all">
            <ArrowLeft className="w-4 h-4" /> All Categories
        </button>

        <div className="surface-solid p-5 flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
                <span className="font-heading text-2xl text-white">{cat.name}</span>
                {cat.hidden && <HiddenTag />}
            </div>
            <HideToggle hidden={cat.hidden} busy={busy} testid="boneyard-hide-current-cat" onClick={onHideCategory} />
        </div>

        <div className="space-y-3">
            {cat.sub.length === 0 && (
                <p className="text-storm-mist text-sm">No subcategories yet.</p>
            )}
            {cat.sub.map((s) => (
                <div key={s.slug} className={`surface-solid p-4 flex items-center justify-between gap-4 ${s.hidden ? "opacity-50" : ""}`} data-testid={`boneyard-sub-${cat.slug}-${s.slug}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        {s.image ? (
                            <img src={s.image} alt="" className="w-12 h-12 object-cover border border-white/10 shrink-0" />
                        ) : (
                            <div className="w-12 h-12 bg-storm-void border border-white/10 flex items-center justify-center text-storm-mist text-[9px] shrink-0">none</div>
                        )}
                        <div className="min-w-0">
                            <div className="text-white/90 text-sm flex items-center gap-2 flex-wrap">
                                <span className="truncate">{s.name}</span>
                                {s.hidden && <HiddenTag />}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <label className="text-storm-bolt hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-wider cursor-pointer">
                            <ImagePlus className="w-4 h-4" /> {s.image ? "Change" : "Photo"}
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                data-testid={`boneyard-subimg-${cat.slug}-${s.slug}`}
                                onChange={(e) => e.target.files?.[0] && onUploadImage(s.slug, e.target.files[0])}
                            />
                        </label>
                        <HideToggle hidden={s.hidden} busy={busy} small testid={`boneyard-hide-sub-${cat.slug}-${s.slug}`} onClick={() => onHideSub(s)} />
                    </div>
                </div>
            ))}
        </div>

        <div className="pt-4">
            <AddInline placeholder="New subcategory name" busy={busy} onAdd={onAddSub} testid={`boneyard-add-sub-${cat.slug}`} />
        </div>
    </div>
);

const ThemeList = ({ themes, busy, onHide, onAdd, onUploadImage }) => (
    <div className="max-w-3xl">
        <div className="space-y-3 mb-4">
            {themes.map((t) => (
                <div key={t.slug} className={`surface-solid p-4 flex items-center justify-between gap-4 ${t.hidden ? "opacity-50" : ""}`} data-testid={`boneyard-theme-${t.slug}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        {t.image ? (
                            <img src={t.image} alt="" className="w-12 h-12 object-cover border border-white/10 shrink-0" />
                        ) : (
                            <div className="w-12 h-12 bg-storm-void border border-white/10 flex items-center justify-center text-storm-mist text-[9px] shrink-0">none</div>
                        )}
                        <div className="text-white/90 text-sm flex items-center gap-2 flex-wrap min-w-0">
                            <span className="truncate">{t.name}</span>
                            {t.hidden && <HiddenTag />}
                        </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <label className="text-storm-bolt hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-wider cursor-pointer">
                            <ImagePlus className="w-4 h-4" /> {t.image ? "Change" : "Photo"}
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                data-testid={`boneyard-themeimg-${t.slug}`}
                                onChange={(e) => e.target.files?.[0] && onUploadImage(t.slug, e.target.files[0])}
                            />
                        </label>
                        <HideToggle hidden={t.hidden} busy={busy} small testid={`boneyard-hide-theme-${t.slug}`} onClick={() => onHide(t)} />
                    </div>
                </div>
            ))}
            {themes.length === 0 && <span className="text-storm-mist text-sm">No themes yet.</span>}
        </div>
        <AddInline placeholder="New theme name" busy={busy} onAdd={onAdd} testid="boneyard-add-theme" />
    </div>
);

export default BoneyardPage;
